import React, { useMemo, useState } from "react";
import {
  Layers3,
  Plus,
  Search,
  Pencil,
  Trash2,
  X,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import "../../styles/ServiceCategories.css";

const initialCategories = [
  {
    id: 1,
    name: "Packing Services",
    description: "Packing and protection of household and office items",
    serviceCount: 8,
    status: "Active",
  },
  {
    id: 2,
    name: "Loading & Unloading",
    description: "Loading and unloading of goods during relocation",
    serviceCount: 5,
    status: "Active",
  },
  {
    id: 3,
    name: "Transportation",
    description: "Transportation services for household and commercial goods",
    serviceCount: 6,
    status: "Active",
  },
  {
    id: 4,
    name: "Furniture Services",
    description: "Furniture dismantling, assembly and handling services",
    serviceCount: 4,
    status: "Active",
  },
  {
    id: 5,
    name: "Storage",
    description: "Short-term and long-term storage solutions",
    serviceCount: 3,
    status: "Active",
  },
  {
    id: 6,
    name: "Vehicle Transportation",
    description: "Car, bike and other vehicle transportation services",
    serviceCount: 3,
    status: "Active",
  },
  {
    id: 7,
    name: "Cleaning Services",
    description: "Pre-move and post-move cleaning services",
    serviceCount: 2,
    status: "Inactive",
  },
  {
    id: 8,
    name: "Additional Services",
    description: "Extra services requested by customers",
    serviceCount: 5,
    status: "Active",
  },
];

export default function ServiceCategories() {
  const [categories, setCategories] = useState(
    initialCategories
  );

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  const rowsPerPage = 6;

  const filteredCategories = useMemo(() => {
    return categories.filter((item) => {
      const searchValue = search.toLowerCase();

      const matchesSearch =
        item.name.toLowerCase().includes(searchValue) ||
        item.description.toLowerCase().includes(searchValue);

      const matchesStatus =
        !statusFilter || item.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [categories, search, statusFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredCategories.length / rowsPerPage)
  );

  const currentPage = Math.min(page, totalPages);

  const currentRows = filteredCategories.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const activeCount = categories.filter(
    (item) => item.status === "Active"
  ).length;

  const inactiveCount = categories.filter(
    (item) => item.status === "Inactive"
  ).length;

  const totalServices = categories.reduce(
    (total, item) => total + item.serviceCount,
    0
  );

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPage(1);
  };

  const toggleStatus = (id) => {
    setCategories((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              status:
                item.status === "Active"
                  ? "Inactive"
                  : "Active",
            }
          : item
      )
    );
  };

  const deleteCategory = (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this service category?"
    );

    if (!confirmed) return;

    setCategories((prev) =>
      prev.filter((item) => item.id !== id)
    );
  };

  const handleAdd = () => {
    alert(
      "Add Service Category form will be connected here."
    );
  };

  const handleEdit = (item) => {
    alert(`Edit Service Category: ${item.name}`);
  };

  return (
    <div className="service-categories-page">

      {/* Header */}
      <div className="service-categories-header">

        <div className="service-categories-title">

          <div className="service-categories-icon">
            <Layers3 size={22} />
          </div>

          <div>
            <h1>Service Categories</h1>
            <p>
              Manage categories used to organize moving services
            </p>
          </div>

        </div>

        <button
          className="service-category-add-btn"
          onClick={handleAdd}
        >
          <Plus size={18} />
          Add Category
        </button>

      </div>

      {/* Summary */}
      <div className="service-category-summary-grid">

        <div className="service-category-summary-card">

          <div className="service-category-summary-icon total">
            <Layers3 size={20} />
          </div>

          <div>
            <span>Total Categories</span>
            <strong>{categories.length}</strong>
          </div>

        </div>

        <div className="service-category-summary-card">

          <div className="service-category-summary-icon active">
            <CheckCircle2 size={20} />
          </div>

          <div>
            <span>Active Categories</span>
            <strong>{activeCount}</strong>
          </div>

        </div>

        <div className="service-category-summary-card">

          <div className="service-category-summary-icon services">
            <Layers3 size={20} />
          </div>

          <div>
            <span>Total Services</span>
            <strong>{totalServices}</strong>
          </div>

        </div>

        <div className="service-category-summary-card">

          <div className="service-category-summary-icon inactive">
            <XCircle size={20} />
          </div>

          <div>
            <span>Inactive Categories</span>
            <strong>{inactiveCount}</strong>
          </div>

        </div>

      </div>

      {/* Filters */}
      <div className="service-category-filter-card">

        <div className="service-category-search">

          <Search size={17} />

          <input
            type="text"
            placeholder="Search service category..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />

        </div>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>

        {(search || statusFilter) && (
          <button
            className="service-category-clear-btn"
            onClick={clearFilters}
          >
            <X size={15} />
            Clear
          </button>
        )}

      </div>

      {/* Desktop Table */}
      <div className="service-category-table-card">

        <div className="service-category-table-wrapper">

          <table className="service-category-table">

            <thead>
              <tr>
                <th>#</th>
                <th>Category</th>
                <th>Description</th>
                <th>Services</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>

              {currentRows.length > 0 ? (
                currentRows.map((item, index) => (
                  <tr key={item.id}>

                    <td>
                      {(currentPage - 1) * rowsPerPage +
                        index +
                        1}
                    </td>

                    <td>
                      <div className="service-category-name">

                        <div className="service-category-row-icon">
                          <Layers3 size={16} />
                        </div>

                        <strong>{item.name}</strong>

                      </div>
                    </td>

                    <td>
                      <span className="service-category-description">
                        {item.description}
                      </span>
                    </td>

                    <td>
                      <span className="service-count-badge">
                        {item.serviceCount} Services
                      </span>
                    </td>

                    <td>

                      <button
                        className={`service-category-status ${
                          item.status === "Active"
                            ? "active"
                            : "inactive"
                        }`}
                        onClick={() =>
                          toggleStatus(item.id)
                        }
                      >
                        {item.status}
                      </button>

                    </td>

                    <td>

                      <div className="service-category-actions">

                        <button
                          className="service-category-action edit"
                          title="Edit"
                          onClick={() =>
                            handleEdit(item)
                          }
                        >
                          <Pencil size={16} />
                        </button>

                        <button
                          className="service-category-action delete"
                          title="Delete"
                          onClick={() =>
                            deleteCategory(item.id)
                          }
                        >
                          <Trash2 size={16} />
                        </button>

                      </div>

                    </td>

                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="service-category-empty"
                  >
                    No service categories found
                  </td>
                </tr>
              )}

            </tbody>

          </table>

        </div>

        {/* Pagination */}
        <div className="service-category-pagination">

          <span>
            Showing{" "}
            {filteredCategories.length === 0
              ? 0
              : (currentPage - 1) * rowsPerPage + 1}{" "}
            -{" "}
            {Math.min(
              currentPage * rowsPerPage,
              filteredCategories.length
            )}{" "}
            of {filteredCategories.length}
          </span>

          <div className="service-category-pagination-buttons">

            <button
              disabled={currentPage === 1}
              onClick={() =>
                setPage((prev) =>
                  Math.max(1, prev - 1)
                )
              }
            >
              Previous
            </button>

            {Array.from(
              { length: totalPages },
              (_, index) => index + 1
            ).map((pageNumber) => (
              <button
                key={pageNumber}
                className={
                  currentPage === pageNumber
                    ? "active"
                    : ""
                }
                onClick={() =>
                  setPage(pageNumber)
                }
              >
                {pageNumber}
              </button>
            ))}

            <button
              disabled={currentPage === totalPages}
              onClick={() =>
                setPage((prev) =>
                  Math.min(
                    totalPages,
                    prev + 1
                  )
                )
              }
            >
              Next
            </button>

          </div>

        </div>

      </div>

      {/* Mobile */}
      <div className="service-category-mobile-list">

        {currentRows.length > 0 ? (
          currentRows.map((item) => (
            <div
              className="service-category-mobile-card"
              key={item.id}
            >

              <div className="service-category-mobile-top">

                <div className="service-category-name">

                  <div className="service-category-row-icon">
                    <Layers3 size={16} />
                  </div>

                  <div>
                    <strong>{item.name}</strong>
                    <span>
                      {item.description}
                    </span>
                  </div>

                </div>

                <button
                  className={`service-category-status ${
                    item.status === "Active"
                      ? "active"
                      : "inactive"
                  }`}
                  onClick={() =>
                    toggleStatus(item.id)
                  }
                >
                  {item.status}
                </button>

              </div>

              <div className="service-category-mobile-info">

                <span>
                  {item.serviceCount} Services
                </span>

              </div>

              <div className="service-category-mobile-actions">

                <button
                  className="mobile-service-category-edit"
                  onClick={() =>
                    handleEdit(item)
                  }
                >
                  <Pencil size={15} />
                  Edit
                </button>

                <button
                  className="mobile-service-category-delete"
                  onClick={() =>
                    deleteCategory(item.id)
                  }
                >
                  <Trash2 size={15} />
                  Delete
                </button>

              </div>

            </div>
          ))
        ) : (
          <div className="service-category-mobile-empty">
            No service categories found
          </div>
        )}

      </div>

    </div>
  );
}